// components/admin/AdminTimePreferenceForm.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AdminTimePreferenceService from '../../services/admin.time-preference.service';

const AdminTimePreferenceForm = ({ userId }) => {
    const [preferences, setPreferences] = useState({
        morning: false,
        afternoon: false,
        evening: false,
        flexible: false
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [noPreferences, setNoPreferences] = useState(false);

    useEffect(() => {
        const loadPreferences = async () => {
            try {
                setLoading(true);
                setError('');
                
                const response = await AdminTimePreferenceService.getUserPreference(userId);
                
                // Se il backend indica che non ci sono preferenze
                if (response.no_preferences) {
                    setNoPreferences(true);
                } else {
                    setNoPreferences(false);
                }
                
                // Imposta comunque i valori delle preferenze
                setPreferences({
                    morning: response.morning,
                    afternoon: response.afternoon,
                    evening: response.evening,
                    flexible: response.flexible
                });
                
            } catch (error) {
                setError('Errore nel caricamento delle preferenze orarie');
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        if (userId) {
            loadPreferences();
        }
    }, [userId]);

    const handleChange = (e) => {
        const { name, checked } = e.target;
        setPreferences(prev => ({
            ...prev,
            [name]: checked
        }));
        
        // Se l'utente interagisce con il form, non siamo più nella condizione "nessuna preferenza"
        if (noPreferences) {
            setNoPreferences(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            await AdminTimePreferenceService.updateUserPreference(userId, preferences);
            setSuccess('Preferenze orarie aggiornate con successo');
            setNoPreferences(false);
        } catch (error) {
            setError('Errore durante il salvataggio delle preferenze orarie');
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div>Caricamento preferenze orarie...</div>;
    }

    return (
        <div className="bg-white p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-4">Preferenze Orarie</h2>
            
            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                    {error}
                </div>
            )}
            
            {success && (
                <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-md">
                    {success}
                </div>
            )}
            
            {noPreferences && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded-md">
                    Non è stata specificata nessuna fascia oraria
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="space-y-3 mb-4">
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            name="morning"
                            checked={preferences.morning}
                            onChange={handleChange}
                            className="h-5 w-5 rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                        />
                        <span>Mattina (09:00 - 13:00)</span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            name="afternoon"
                            checked={preferences.afternoon}
                            onChange={handleChange}
                            className="h-5 w-5 rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                        />
                        <span>Pomeriggio (14:00 - 18:00)</span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            name="evening"
                            checked={preferences.evening}
                            onChange={handleChange}
                            className="h-5 w-5 rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                        />
                        <span>Sera (19:00 - 21:00)</span>
                    </label>
                    
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            name="flexible"
                            checked={preferences.flexible}
                            onChange={handleChange}
                            className="h-5 w-5 rounded border-gray-300 text-purple-500 focus:ring-purple-500"
                        />
                        <span>Flessibile (qualsiasi orario)</span>
                    </label>
                </div>

                <button
                    type="submit"
                    disabled={saving}
                    className="w-full py-2 px-4 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50"
                >
                    {saving ? 'Salvataggio in corso...' : 'Salva Preferenze'}
                </button>
            </form>
        </div>
    );
};

AdminTimePreferenceForm.propTypes = {
    userId: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired
};

export default AdminTimePreferenceForm;